import { cleanTitle, isIntentId, isSafeSlug, type IntentId } from "./ids";
import { pageForSlug } from "../app/paths";

/**
 * Contesto d'ingresso della richiesta: da dove arriva chi scrive.
 *
 * - dal box «confezione personalizzata» del carrello: `gb:open-contact` con un
 *   `detail` { intent, items } (le confezioni e le quantità del carrello);
 * - dalla scheda di una confezione: lo slug è nell'URL;
 * - da nessuna parte: nessun default.
 *
 * Il `detail` dell'evento lo può emettere chiunque in pagina: si valida tutto e
 * si scarta ciò che non torna. Mai dati personali, mai il consenso.
 */

export interface ContextItem {
  slug: string;
  qty: number | null;
  title: string | null;
}

export interface OpenContext {
  /** cart: box del carrello · page: scheda prodotto · collections: «Vuoi combinare più confezioni?» */
  source: "cart" | "page" | "collections";
  intent: IntentId | null;
  items: ContextItem[];
}

const MAX_ITEMS = 12;

export function parseOpenDetail(detail: unknown): OpenContext | null {
  if (!detail || typeof detail !== "object") return null;
  const d = detail as { intent?: unknown; items?: unknown };
  const intent = isIntentId(d.intent) ? d.intent : null;
  const items: ContextItem[] = [];
  if (Array.isArray(d.items)) {
    for (const raw of d.items.slice(0, MAX_ITEMS)) {
      if (!raw || typeof raw !== "object") continue;
      const it = raw as { slug?: unknown; qty?: unknown; title?: unknown };
      if (!isSafeSlug(it.slug)) continue;
      if (items.some((x) => x.slug === it.slug)) continue;
      const qty = typeof it.qty === "number" && Number.isInteger(it.qty) && it.qty >= 1 && it.qty <= 99999 ? it.qty : null;
      const title = cleanTitle(it.title) || null;
      items.push({ slug: it.slug, qty, title });
    }
  }
  if (!intent && items.length === 0) return null;
  return { source: "cart", intent, items };
}

/** Slug della confezione se il percorso è una scheda prodotto (`/it/confezioni/roma`, `/de/kollektionen/roma`). */
export function productSlugFromPath(pathname: string): string | null {
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length !== 3) return null;
  const [, section, slug] = parts;
  if (pageForSlug(section, true) !== "product") return null;
  return isSafeSlug(slug) ? slug : null;
}

export function contextFromPath(pathname: string): OpenContext | null {
  const slug = productSlugFromPath(pathname);
  return slug ? { source: "page", intent: null, items: [{ slug, qty: null, title: null }] } : null;
}

/**
 * Contesto passato con lo stato della navigazione (React Router) verso la
 * pagina Contatti: oggi solo dalla CTA di Collezioni, che apre l'offerta.
 */
export function contextFromNavState(state: unknown): OpenContext | null {
  const intent = state && typeof state === "object" ? (state as { gbIntent?: unknown }).gbIntent : undefined;
  return intent === "offer" ? { source: "collections", intent: "offer", items: [] } : null;
}

/** La pagina Contatti stessa (`/it/contatti`, `/en/contact`, `/de/kontakt`): lì il modulo c'è già. */
export function isContactPath(pathname: string): boolean {
  const parts = pathname.split("/").filter(Boolean);
  return parts.length === 2 && pageForSlug(parts[1], false) === "contact";
}
